import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { decideRequest } from "@/lib/approvals";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const LABEL = {
  leave: "Leave",
  regularization: "Regularisation",
  approval: "Profile change",
};

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const decide = async (item, approve) => {
    setBusy(item.id);
    try {
      await decideRequest(item.kind, item.id, approve);
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500">Loading...</div>;

  const waiting = items.filter((i) => i.actionable);
  const recent = items.filter((i) => !i.actionable);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Notifications</h1>
        <div className="text-sm text-zinc-500">
          {waiting.length
            ? `${waiting.length} request${waiting.length === 1 ? "" : "s"} waiting on you`
            : "Nothing waiting on you"}
        </div>
      </div>

      {waiting.length > 0 && (
        <Card className="border-zinc-200 shadow-none">
          <CardContent className="p-0 divide-y divide-zinc-100">
            {waiting.map((i) => (
              <div
                key={`${i.kind}-${i.id}`}
                className="flex items-start justify-between gap-4 p-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[11px]">
                      {LABEL[i.kind] || i.kind}
                    </Badge>
                    <span className="text-sm font-medium truncate">{i.who}</span>
                    {i.code && (
                      <span className="text-[11px] text-zinc-400">{i.code}</span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-700 mt-1">{i.title}</div>
                  {i.subtitle && (
                    <div className="text-xs text-zinc-500 mt-0.5">{i.subtitle}</div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    disabled={busy === i.id}
                    onClick={() => decide(i, true)}
                    className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy === i.id}
                    onClick={() => decide(i, false)}
                    className="h-7 text-xs"
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {recent.length > 0 && (
        <div>
          <div className="text-[12px] text-zinc-500 mb-2">Recent activity</div>
          <Card className="border-zinc-200 shadow-none">
            <CardContent className="p-0 divide-y divide-zinc-100">
              {recent.map((i) => (
                <div key={`${i.kind}-${i.id}`} className="p-4">
                  <div className="text-sm text-zinc-700">{i.title}</div>
                  {i.subtitle && (
                    <div className="text-xs text-zinc-500 mt-0.5">{i.subtitle}</div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {!waiting.length && !recent.length && (
        <div className="text-sm text-zinc-500">You are all caught up.</div>
      )}
    </div>
  );
}
